import { Box, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import NumberInput from '../../../shared/UI/NumberInput'
import FullComponentLoadingIcon from '../../../shared/UI/LoadingIndicator/FullComponentLoadingIcon'
import { removeTrailingZeroes } from '../../../utils/conversion'
import { AbsenceOverviewData } from './types'

type AbsenceOverviewCardProps = {
  data: AbsenceOverviewData | null
  loading?: boolean
  onViewDetail: () => void
  onApprovalHoursChange?: (value: number | null) => void
  approvalHoursValue?: number | null
  showDiscretionaryPay: boolean
}

function AbsenceOverviewCard({
  data,
  loading = false,
  onViewDetail,
  onApprovalHoursChange,
  approvalHoursValue = null,
  showDiscretionaryPay,
}: AbsenceOverviewCardProps) {
   if (!data) {
     return (
       <Box sx={{ height: '200px', position: 'relative' }}>
         <FullComponentLoadingIcon loading={loading} noData={!loading}><></></FullComponentLoadingIcon>
       </Box>
     )
   }

  const {
    startDate,
    endDate,
    totalAbsenceHours,
    hasSplitPots,
    paidHours,
    fiftyPercentUsed = 0,
    unpaidHours,
    exceededHours,
  } = data

  return (
    <FullComponentLoadingIcon loading={loading}>
      <Box
        data-testid="absence-overview-card"
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #D5D5D5',
          borderRadius: '16px',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '20px',
              letterSpacing: '0.25px',
              color: '#000000',
            }}
          >
            Absence Overview
          </Typography>
          <Box
            component="button"
            onClick={onViewDetail}
            data-testid="absence-overview-view-detail"
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.1px',
              color: '#3C4A44',
              backgroundColor: 'transparent',
              border: '1px solid #BBCAC3',
              borderRadius: '100px',
              padding: '6px 12px',
              cursor: 'pointer',
              outline: 'none',
              '&:hover': {
                backgroundColor: 'rgba(60, 74, 68, 0.04)',
              },
            }}
          >
            View Detail
          </Box>
        </Box>

        {/* Absence Details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* Dates + Total Hours box */}
          <Box
            sx={{
              border: '1px solid #F0F0F0',
              borderRadius: '8px',
              padding: '17px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            {/* Dates */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '24px',
                  letterSpacing: '0.15px',
                  color: '#555555',
                }}
              >
                Dates:
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                  letterSpacing: '0.25px',
                  color: '#555555',
                }}
              >
                {startDate} - {endDate}
              </Typography>
            </Box>

            {/* Total absence hours */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '24px',
                  letterSpacing: '0.15px',
                  color: '#555555',
                }}
              >
                Total absence hours:
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 700,
                  fontSize: '14px',
                  lineHeight: '32px',
                  letterSpacing: '0.15px',
                  color: '#4A4A4A',
                }}
              >
                {Number(totalAbsenceHours).toFixed(2)} hrs
              </Typography>
            </Box>
          </Box>

          {/* Paid row (100% when split pots) */}
          <Box
            data-testid="absence-overview-paid-row"
            sx={{
              backgroundColor: '#EFF6FF',
              borderRadius: '8px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 16px',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '24px',
                letterSpacing: '0.15px',
                color: '#1565C0',
              }}
            >
              {hasSplitPots ? 'Paid at 100% rate' : 'Paid'}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 700,
                fontSize: '14px',
                lineHeight: '24px',
                letterSpacing: '0.15px',
                color: '#1565C0',
              }}
            >
              {Number(paidHours).toFixed(2)} hrs
            </Typography>
          </Box>

          {/* Paid at 50% rate row (split pots only) */}
          {hasSplitPots && (
            <Box
              data-testid="absence-overview-fifty-percent-row"
              sx={{
                backgroundColor: '#FDF7FF',
                borderRadius: '8px',
                borderBottom: '0.5px solid rgba(0, 0, 0, 0.08)',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 16px',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '24px',
                  letterSpacing: '0.15px',
                  color: '#7B1FA2',
                }}
              >
                Paid at 50% rate
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 700,
                  fontSize: '14px',
                  lineHeight: '24px',
                  letterSpacing: '0.15px',
                  color: '#7B1FA2',
                }}
              >
                {Number(fiftyPercentUsed).toFixed(2)} hrs
              </Typography>
            </Box>
          )}
        </Box>

        {/* Discretionary Pay */}
        {showDiscretionaryPay && exceededHours > 0 && (
          <Box
            data-testid="absence-overview-discretionary-pay"
            sx={{
              backgroundColor: '#FCFCFC',
              borderRadius: '16px',
              padding: '16px',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', overflow: 'hidden', paddingBottom: '16px' }}>
              {/* Title */}
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0.1px',
                  color: '#000000',
                }}
              >
                Discretionary Pay
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                {/* Banner + Input */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                  {/* Pay Approval Banner */}
                  <Box
                    data-testid="absence-overview-pay-approval-banner"
                    sx={{
                      backgroundColor: '#FDEFC8',
                      borderRadius: '16px',
                      padding: '12px',
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <InfoOutlinedIcon sx={{ fontSize: '20px', color: '#000000', flexShrink: 0, marginTop: '2px' }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 700,
                            fontSize: '12px',
                            lineHeight: '16px',
                            letterSpacing: '0.4px',
                            color: '#000000',
                          }}
                        >
                          Pay Approval
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 400,
                            fontSize: '11px',
                            lineHeight: '16px',
                            letterSpacing: '0.4px',
                            color: '#000000',
                          }}
                        >
                          The duration of this absence exceeds the employee&apos;s OSP allowance by{' '}
                          <strong>{removeTrailingZeroes(exceededHours, 2)}</strong> hours. You can
                          request approval for up to{' '}
                          <strong>{removeTrailingZeroes(exceededHours, 2)}</strong> of these hours
                          to be paid
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Hours to be paid input */}
                  <NumberInput
                    label="Hours to be paid"
                    id="absence-overview-hours-to-be-paid"
                    max={exceededHours}
                    value={approvalHoursValue}
                    fullWidth
                    onChange={onApprovalHoursChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '56px',
                        borderRadius: '4px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6C7A74',
                      },
                      '& .MuiOutlinedInput-input': {
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '16px',
                        padding: '0 16px',
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                      },
                      '& .MuiInputLabel-root': {
                        fontFamily: 'Poppins, sans-serif',
                        color: '#3C4A44',
                      },
                      '& .MuiFormHelperText-root': {
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '12px',
                        letterSpacing: '0.4px',
                        color: '#3C4A44',
                      },
                    }}
                  />
                </Box>

                {/* Unpaid row */}
                <Box
                  data-testid="absence-overview-unpaid-row"
                  sx={{
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    borderRadius: '8px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 16px',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '24px',
                      letterSpacing: '0.15px',
                      color: '#000000',
                    }}
                  >
                    Unpaid
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 700,
                      fontSize: '14px',
                      lineHeight: '24px',
                      letterSpacing: '0.15px',
                      color: '#000000',
                    }}
                  >
                    {Number(Math.max(unpaidHours - (approvalHoursValue ?? 0), 0)).toFixed(2)} hrs
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </FullComponentLoadingIcon>
  )
}

export default AbsenceOverviewCard
